import axios from 'axios';
/**
 * HACCP API Client
 * Production-grade SDK for interacting with the HACCP backend
 */
export class HaccpApiClient {
    constructor(baseURL = 'http://localhost:3000') {
        this.token = null;
        this.baseURL = baseURL;
        this.client = axios.create({
            baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        // Add request interceptor for token
        this.client.interceptors.request.use((config) => {
            if (this.token) {
                config.headers.Authorization = `Bearer ${this.token}`;
            }
            return config;
        });
    }
    /**
     * Set authentication token
     */
    setToken(token) {
        this.token = token;
    }
    /**
     * Clear authentication token
     */
    clearToken() {
        this.token = null;
    }
    // ========================================================================
    // AUTHENTICATION
    // ========================================================================
    async register(email, password, name) {
        const { data } = await this.client.post('/auth/register', {
            email,
            password,
            name,
        });
        return data;
    }
    async login(email, password) {
        const { data } = await this.client.post('/auth/login', {
            email,
            password,
        });
        return data;
    }
    async getCurrentUser() {
        const { data } = await this.client.get('/auth/me');
        return data;
    }
    async getProfile() {
        const { data } = await this.client.get('/auth/profile');
        return data;
    }
    async updateProfile(profile) {
        const { data } = await this.client.patch('/auth/profile', profile);
        return data;
    }
    async uploadProfilePicture(formData) {
        const { data } = await this.client.post('/auth/profile/picture', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async changePassword(currentPassword, newPassword) {
        const { data } = await this.client.post('/auth/change-password', {
            currentPassword,
            newPassword,
        });
        return data;
    }
    async forgotPassword(email) {
        const { data } = await this.client.post('/auth/forgot-password', { email });
        return data;
    }
    async resetPassword(token, newPassword) {
        const { data } = await this.client.post('/auth/reset-password', {
            token,
            newPassword,
        });
        return data;
    }
    async verifyEmail(token) {
        const { data } = await this.client.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        return data;
    }
    async resendVerification() {
        const { data } = await this.client.post('/auth/resend-verification');
        return data;
    }
    // ========================================================================
    // TEAM MEMBERS
    // ========================================================================
    async getTeamMembers(establishmentId) {
        const { data } = await this.client.get(`/establishments/${establishmentId}/team-members`);
        return data;
    }
    async addTeamMember(establishmentId, member) {
        const { data } = await this.client.post(`/establishments/${establishmentId}/team-members`, member);
        return data;
    }
    async updateTeamMember(establishmentId, userId, updates) {
        const { data } = await this.client.patch(`/establishments/${establishmentId}/team-members/${userId}`, updates);
        return data;
    }
    async removeTeamMember(establishmentId, userId) {
        const { data } = await this.client.delete(`/establishments/${establishmentId}/team-members/${userId}`);
        return data;
    }
    async resetTeamMemberPassword(establishmentId, userId, newPassword) {
        const { data } = await this.client.post(`/establishments/${establishmentId}/team-members/${userId}/reset-password`, { newPassword });
        return data;
    }
    // ========================================================================
    // DASHBOARD
    // ========================================================================
    async getDashboardSummary() {
        const { data } = await this.client.get('/dashboard/summary');
        return data;
    }
    // ========================================================================
    // TEMPERATURE LOGS
    // ========================================================================
    async createTemperatureLog(equipmentId, temperatureC, measuredAt) {
        const { data } = await this.client.post('/temperature-logs', {
            equipmentId,
            temperatureC,
            measuredAt,
        });
        return data;
    }
    async getTemperatureLogs(skip = 0, take = 50) {
        const { data } = await this.client.get('/temperature-logs', { params: { skip, take } });
        // Ensure data field is always an array
        if (data && !Array.isArray(data.data)) {
            return { ...data, data: Array.isArray(data) ? data : [] };
        }
        return data;
    }
    // ========================================================================
    // EQUIPMENT
    // ========================================================================
    async getEquipment() {
        const { data } = await this.client.get('/equipment');
        return Array.isArray(data) ? data : [];
    }
    async createEquipment(name, type, minTempC, maxTempC, sectorId) {
        const { data } = await this.client.post('/equipment', {
            name,
            type,
            minTempC,
            maxTempC,
            ...(sectorId ? { sectorId } : {}),
        });
        return data;
    }
    async updateEquipment(id, updates) {
        const { data } = await this.client.patch(`/equipment/${id}`, updates);
        return data;
    }
    async deleteEquipment(id) {
        await this.client.delete(`/equipment/${id}`);
    }
    async getEquipmentTodayStatus() {
        const { data } = await this.client.get('/equipment/today-status');
        return Array.isArray(data) ? data : [];
    }
    // ========================================================================
    // EQUIPMENT SECTORS
    // ========================================================================
    async getEquipmentSectors() {
        const { data } = await this.client.get('/equipment/sectors');
        return Array.isArray(data) ? data : [];
    }
    async createEquipmentSector(name, order) {
        const { data } = await this.client.post('/equipment/sectors', { name, order });
        return data;
    }
    async updateEquipmentSector(id, body) {
        const { data } = await this.client.patch(`/equipment/sectors/${id}`, body);
        return data;
    }
    async deleteEquipmentSector(id) {
        await this.client.delete(`/equipment/sectors/${id}`);
    }
    async assignEquipmentToSector(equipmentId, sectorId) {
        const { data } = await this.client.patch(`/equipment/${equipmentId}/sector`, { sectorId });
        return data;
    }
    async findOrCreateEquipmentSector(name) {
        try {
            return await this.createEquipmentSector(name);
        }
        catch (err) {
            if (err?.response?.status !== 409)
                throw err;
            const sectors = await this.getEquipmentSectors();
            const existing = sectors.find((s) => s.name === name);
            if (!existing)
                throw new Error(`Impossible de créer le secteur "${name}"`);
            return existing;
        }
    }
    // ========================================================================
    // CLEANING
    // ========================================================================
    async getCleaningSectors() {
        const { data } = await this.client.get('/cleaning/sectors');
        return Array.isArray(data) ? data : [];
    }
    async createCleaningSector(name, icon, order) {
        const { data } = await this.client.post('/cleaning/sectors', { name, icon, order });
        return data;
    }
    async updateCleaningSector(id, body) {
        const { data } = await this.client.patch(`/cleaning/sectors/${id}`, body);
        return data;
    }
    async deleteCleaningSector(id) {
        await this.client.delete(`/cleaning/sectors/${id}`);
    }
    async addEquipmentToSector(sectorId, name) {
        const { data } = await this.client.post(`/cleaning/sectors/${sectorId}/equipment`, { name });
        return data;
    }
    async addTaskToSector(sectorId, name) {
        const { data } = await this.client.post(`/cleaning/sectors/${sectorId}/tasks`, { name });
        return data;
    }
    async createCleaningSubSector(sectorId, name, icon, order) {
        const { data } = await this.client.post('/cleaning/subsectors', { sectorId, name, icon, order });
        return data;
    }
    async deleteCleaningSubSector(id) {
        await this.client.delete(`/cleaning/subsectors/${id}`);
    }
    async createCleaningEquipment(sectorId, name, order) {
        const { data } = await this.client.post('/cleaning/equipment', { sectorId, name, order });
        return data;
    }
    async updateCleaningEquipment(id, body) {
        const { data } = await this.client.patch(`/cleaning/equipment/${id}`, body);
        return data;
    }
    async deleteCleaningEquipment(id) {
        await this.client.delete(`/cleaning/equipment/${id}`);
    }
    async createCleaningTask(equipmentId, name, frequency, order) {
        const { data } = await this.client.post('/cleaning/tasks', { equipmentId, name, frequency, order });
        return data;
    }
    async updateCleaningTask(id, body) {
        const { data } = await this.client.patch(`/cleaning/tasks/${id}`, body);
        return data;
    }
    async deleteCleaningTask(id) {
        await this.client.delete(`/cleaning/tasks/${id}`);
    }
    async getCleaningToday() {
        const { data } = await this.client.get('/cleaning/today');
        return data;
    }
    async checkCleaningTask(planId, taskId) {
        const { data } = await this.client.post(`/cleaning/plans/${planId}/check`, { taskId });
        return data;
    }
    async uncheckCleaningTask(planId, taskId) {
        await this.client.delete(`/cleaning/plans/${planId}/check/${taskId}`);
    }
    async completeCleaningPlan(planId) {
        const { data } = await this.client.post(`/cleaning/plans/${planId}/complete`, {});
        return data;
    }
    async getCleaningHistory() {
        const { data } = await this.client.get('/cleaning/history');
        return Array.isArray(data) ? data : [];
    }
    // ========================================================================
    // OIL CHECKS
    // ========================================================================
    async getOilStations() {
        const { data } = await this.client.get('/oil/stations');
        return data;
    }
    async createOilStation(name) {
        const { data } = await this.client.post('/oil/stations', { name });
        return data;
    }
    async recordOilCheck(stationId, quality, oilChanged, method, notes, polarity) {
        const { data } = await this.client.post('/oil/checks', {
            stationId,
            quality,
            oilChanged,
            polarity,
            method,
            notes,
        });
        return data;
    }
    async getOilCheckHistory(stationId) {
        const { data } = await this.client.get(`/oil/stations/${stationId}/history`);
        return data;
    }
    // ========================================================================
    // INCIDENTS
    // ========================================================================
    async getIncidents(skip = 0, take = 50) {
        const { data } = await this.client.get('/incidents', { params: { skip, take } });
        if (data && !Array.isArray(data.data)) {
            return { ...data, data: [] };
        }
        return data;
    }
    async getIncidentById(id) {
        const { data } = await this.client.get(`/incidents/${id}`);
        return data;
    }
    async addCorrectiveAction(incidentId, actionText) {
        const { data } = await this.client.post(`/incidents/${incidentId}/corrective-actions`, {
            actionText,
        });
        return data;
    }
    async closeIncident(incidentId, correctiveTemperatureC) {
        const body = correctiveTemperatureC !== undefined ? { correctiveTemperatureC } : {};
        const { data } = await this.client.post(`/incidents/${incidentId}/close`, body);
        return data;
    }
    // ========================================================================
    // SUPPORT TICKETS
    // ========================================================================
    async createSupportTicket(category, message) {
        const { data } = await this.client.post('/support/tickets', {
            category,
            message,
        });
        return data;
    }
    async getSupportTickets() {
        const { data } = await this.client.get('/support/tickets');
        return Array.isArray(data) ? data : [];
    }
    // ========================================================================
    // SUBSCRIPTIONS & BILLING
    // ========================================================================
    async getSubscription() {
        const { data } = await this.client.get('/subscriptions/current');
        return data;
    }
    /**
     * Create a Stripe Checkout Session for a subscription plan.
     * Returns a URL to redirect the user to Stripe's hosted checkout page.
     */
    async createCheckoutSession(successUrl, cancelUrl, plan) {
        const { data } = await this.client.post('/billing/create-checkout-session', {
            successUrl,
            cancelUrl,
            plan,
        });
        return data;
    }
    /**
     * Create a Stripe Customer Portal session for self-service billing management.
     * Returns a URL to redirect the user to Stripe's customer portal.
     */
    async createCustomerPortalSession(returnUrl) {
        const { data } = await this.client.post('/billing/customer-portal', {
            returnUrl,
        });
        return data;
    }
    // ========================================================================
    // EXPORTS
    // ========================================================================
    async generateExport(periodStart, periodEnd) {
        const { data } = await this.client.post('/exports/generate', {
            periodStart,
            periodEnd,
        });
        return data;
    }
    async getExportDownloadUrl(exportId) {
        const { data } = await this.client.get(`/exports/${exportId}/download`);
        return data;
    }
    // ========================================================================
    // PMS DOCUMENTS
    // ========================================================================
    async getPmsDocument() {
        const { data } = await this.client.get('/pms');
        return data || null;
    }
    async getPmsDocumentById(documentId) {
        const { data } = await this.client.get(`/pms/${documentId}`);
        return data;
    }
    async uploadPmsDocument(file, notes) {
        const formData = new FormData();
        formData.append('file', file);
        if (notes)
            formData.append('notes', notes);
        const { data } = await this.client.post('/pms/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async getPmsDownloadUrl(documentId) {
        const { data } = await this.client.get(`/pms/${documentId}/download-url`);
        return data;
    }
    async deletePmsDocument(documentId) {
        await this.client.delete(`/pms/${documentId}`);
    }
    async updatePmsDocumentNotes(documentId, notes) {
        const { data } = await this.client.patch(`/pms/${documentId}/notes`, { notes });
        return data;
    }
    async getExports() {
        const { data } = await this.client.get('/exports');
        return data || [];
    }
    // ========================================================================
    // NOTIFICATIONS
    // ========================================================================
    async getNotifications() {
        const { data } = await this.client.get('/notifications/in-app');
        return Array.isArray(data) ? data : [];
    }
    async getNotificationHistory() {
        const { data } = await this.client.get('/notifications/history');
        return Array.isArray(data) ? data : [];
    }
    async markNotificationRead(notificationId) {
        const { data } = await this.client.post(`/notifications/${notificationId}/mark-read`);
        return data;
    }
    // ========================================================================
    // ADMIN ONLY
    // ========================================================================
    async getAdminSubscriptions(skip = 0, take = 50) {
        const { data } = await this.client.get('/admin/subscriptions', { params: { skip, take } });
        // Normalize response to use 'data' key for consistency
        return { data: data.subscriptions, total: data.total, skip: data.skip, take: data.take };
    }
    async getAdminEstablishments(skip = 0, take = 50) {
        const { data } = await this.client.get('/admin/establishments', {
            params: { skip, take },
        });
        // Normalize response to use 'data' key for consistency
        return { data: data.establishments, total: data.total, skip: data.skip, take: data.take };
    }
    async getAdminUsers() {
        const { data } = await this.client.get('/admin/users');
        return data.users ?? [];
    }
    async toggleUserSuspended(userId) {
        const { data } = await this.client.patch(`/admin/users/${userId}/suspend`);
        return data;
    }
    async getAuditLogs(skip = 0, take = 50, filters) {
        const { data } = await this.client.get('/admin/audit-logs', {
            params: { skip, take, ...filters },
        });
        // Normalize response to use 'data' key for consistency
        return { data: data.logs, total: data.total, skip: data.skip, take: data.take };
    }
    async getRevenueMetrics() {
        const { data } = await this.client.get('/admin/metrics/revenue');
        return data;
    }
    async getSystemStats() {
        const { data } = await this.client.get('/admin/metrics/system');
        return data;
    }
    async getAdminSupportTickets(skip = 0, take = 50) {
        const { data } = await this.client.get('/admin/support-tickets', {
            params: { skip, take },
        });
        // Normalize response to use 'data' key for consistency
        return { data: data.tickets, total: data.total, skip: data.skip, take: data.take };
    }
    async getAdminSupportTicketById(ticketId) {
        const { data } = await this.client.get(`/admin/support-tickets/${ticketId}`);
        return data;
    }
    async updateAdminSupportTicketStatus(ticketId, status) {
        const { data } = await this.client.patch(`/admin/support-tickets/${ticketId}/status`, { status });
        return data;
    }
    async getBillingDashboard() {
        const { data } = await this.client.get('/admin/billing/dashboard');
        return data;
    }
    // ========================================================================
    // TRACEABILITY / PRODUCTS
    // ========================================================================
    async getProducts(skip = 0, take = 20) {
        const { data } = await this.client.get('/traceability/products', { params: { skip, take } });
        return {
            data: data?.products ?? [],
            total: data?.total ?? 0,
            skip,
            take,
        };
    }
    async getProductById(id) {
        const { data } = await this.client.get(`/traceability/products/${id}`);
        return data;
    }
    async createProduct(productData) {
        const { data } = await this.client.post('/traceability/products', productData);
        return data;
    }
    async deleteProduct(id) {
        await this.client.delete(`/traceability/products/${id}`);
    }
    async addProductPhoto(productId, formData) {
        const { data } = await this.client.post(`/traceability/products/${productId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return data;
    }
    async searchProductsBySupplier(supplier) {
        const { data } = await this.client.get(`/traceability/products/search/supplier/${supplier}`);
        return data?.products ?? [];
    }
    async getExpiringProducts() {
        const { data } = await this.client.get('/traceability/products/expiring/soon');
        return data?.expiringProducts ?? data?.products ?? [];
    }
    // ========================================================================
    // SUPPLIERS
    // ========================================================================
    async getSuppliers() {
        const { data } = await this.client.get('/suppliers');
        return Array.isArray(data) ? data : [];
    }
    async getSupplier(id) {
        const { data } = await this.client.get(`/suppliers/${id}`);
        return data;
    }
    async createSupplier(supplier) {
        const { data } = await this.client.post('/suppliers', supplier);
        return data;
    }
    async updateSupplier(id, supplier) {
        const { data } = await this.client.patch(`/suppliers/${id}`, supplier);
        return data;
    }
    async deleteSupplier(id) {
        await this.client.delete(`/suppliers/${id}`);
    }
    // ========================================================================
    // GOODS RECEPTION
    // ========================================================================
    async getReceptions(skip = 0, take = 50) {
        const { data } = await this.client.get('/reception', { params: { skip, take } });
        return {
            data: data?.receptions ?? [],
            total: data?.total ?? 0,
            skip,
            take,
        };
    }
    async getReception(id) {
        const { data } = await this.client.get(`/reception/${id}`);
        return data;
    }
    async createReception(reception) {
        const { data } = await this.client.post('/reception', reception);
        return data;
    }
    async addReceptionPhoto(receptionId, formData) {
        const { data } = await this.client.post(`/reception/${receptionId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        return data;
    }
    async deleteReception(id) {
        await this.client.delete(`/reception/${id}`);
    }
    async updateReception(id, data) {
        const { data: result } = await this.client.patch(`/reception/${id}`, data);
        return result;
    }
    // ========================================================================
    // DLC (DATE LIMITE DE CONSOMMATION)
    // ========================================================================
    // Internal Preparations (Owner functions)
    async createInternalPreparation(preparation) {
        const { data } = await this.client.post('/dlc/preparations', preparation);
        return data;
    }
    async listInternalPreparations() {
        const { data } = await this.client.get('/dlc/preparations');
        return Array.isArray(data) ? data : [];
    }
    async getInternalPreparation(id) {
        const { data } = await this.client.get(`/dlc/preparations/${id}`);
        return data;
    }
    async updateInternalPreparation(id, preparation) {
        const { data } = await this.client.put(`/dlc/preparations/${id}`, preparation);
        return data;
    }
    async deleteInternalPreparation(id) {
        await this.client.delete(`/dlc/preparations/${id}`);
    }
    // Preparation Batches (Staff functions)
    async createBatch(batch) {
        const { data } = await this.client.post('/dlc/batches', batch);
        return data;
    }
    async createRecipe(formData) {
        const { data } = await this.client.post('/dlc/recipes', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async listBatches(filters) {
        const { data } = await this.client.get('/dlc/batches', { params: filters });
        return Array.isArray(data) ? data : [];
    }
    async getBatch(id) {
        const { data } = await this.client.get(`/dlc/batches/${id}`);
        return data;
    }
    async getBatchByBatchId(batchId) {
        const { data } = await this.client.get(`/dlc/batches/by-batch-id/${batchId}`);
        return data;
    }
    async destroyBatch(id) {
        const { data } = await this.client.post(`/dlc/batches/${id}/destroy`);
        return data;
    }
    async updateBatchStatuses() {
        await this.client.post('/dlc/batches/update-statuses');
    }
    // Label & QR Code
    getQRCodeUrl(batchId) {
        return `${this.baseURL}/dlc/batches/${batchId}/qr-code`;
    }
    getLabelUrl(batchId) {
        return `${this.baseURL}/dlc/batches/${batchId}/label`;
    }
    async downloadLabel(batchId) {
        const response = await this.client.get(`/dlc/batches/${batchId}/label`, {
            responseType: 'blob',
        });
        return response.data;
    }
    // ========================================================================
    // TASKS
    // ========================================================================
    // ========================================================================
    // TEMPERATURE TRACKING
    // ========================================================================
    async createTempTracking(formData) {
        const { data } = await this.client.post('/temp-tracking', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return data;
    }
    async listTempTrackings() {
        const { data } = await this.client.get('/temp-tracking');
        return Array.isArray(data) ? data : [];
    }
    async updateTempTracking(id, body) {
        const { data } = await this.client.put(`/temp-tracking/${id}`, body);
        return data;
    }
    async deleteTempTracking(id) {
        await this.client.delete(`/temp-tracking/${id}`);
    }
    async getPendingTasks() {
        const { data } = await this.client.get('/tasks/pending');
        return Array.isArray(data) ? data : [];
    }
    async getTasksHistory(days = 30) {
        const { data } = await this.client.get('/tasks/history', {
            params: { days },
        });
        return Array.isArray(data) ? data : [];
    }
}
